import React from 'react';
import { Text } from '@cad-arc';
import { omitRef } from '../helpers/omit-ref';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';
import { AttributeSelect } from '../inputs/attribute-select';
import { FieldPath, FieldValues } from 'react-hook-form';
import { OptionOrOptions } from '../option';

type AttributeSelectProps = React.ComponentProps<typeof AttributeSelect>;
type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    attributeType: AttributeSelectProps['attributeType'];
    isMultiple?: boolean;
    attributeSelectProps?: Partial<AttributeSelectProps>;
};

export function FormAttributeSelect<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const {
        attributeSelectProps,
        isMultiple,
        attributeType,
        ...integratedFormControlProps
    } = props;
    const attributeSelectPropsMemo = React.useMemo(() => attributeSelectProps ?? {}, [
        attributeSelectProps,
    ]);
    const renderViewMode = React.useCallback((value: OptionOrOptions) => {
        if (Array.isArray(value)) {
            if (value.length === 0) return <Text>--</Text>;
            return <Text>{value.map((option) => option.label).join(', ')}</Text>;
        }
        return <Text>{value.label}</Text>;
    }, []);
    return (
        <IntegratedFormControl<OptionOrOptions>
            {...integratedFormControlProps}
            renderViewMode={renderViewMode}
        >
            {(inputProps) => (
                <AttributeSelect
                    {...omitRef(inputProps)}
                    attributeType={attributeType}
                    isMultiple={isMultiple}
                    {...attributeSelectPropsMemo}
                />
            )}
        </IntegratedFormControl>
    );
}
